import { computed, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { filter, map, type Observable, take } from 'rxjs';

import { AbilitiesContext } from '#core/context/abilities.context';
import { OrganizationPermissionsService } from '#core/services/organization-permissions.service';
import { SessionStore } from '#core/store/session.store';
import { RestaurantsStore } from '#modules/restaurants/stores/restaurants.store';

/**
 * Guards the organization users & roles page: either tab readable lets the user in, neither sends them back to the settings.
 *
 * It decides only once the abilities and the restaurants are loaded, the session has settled and an organization is selected — never before.
 * The abilities start empty, the session user loads in parallel with them and decides the admin bypass, and the organization is auto-selected in an effect once the restaurants land, so an earlier read would mistake "not ready" for "not allowed".
 * A user with no restaurant at all never gets an organization selected; the guard then decides on the empty state rather than waiting forever.
 */
export const organizationUsersReadGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const injector = inject(Injector);
    const abilitiesContext = inject(AbilitiesContext);
    const organizationPermissionsService = inject(OrganizationPermissionsService);
    const restaurantsStore = inject(RestaurantsStore);
    const sessionStore = inject(SessionStore);

    const isReady = computed(
        () =>
            abilitiesContext.areAbilitiesLoaded() &&
            restaurantsStore.isLoaded() &&
            (sessionStore.isLoaded() || sessionStore.isError()) &&
            (restaurantsStore.selectedOrganizationId() !== null || restaurantsStore.restaurants().length === 0)
    );

    return toObservable(isReady, { injector }).pipe(
        filter(Boolean),
        take(1),
        map(() => {
            const organizationId = restaurantsStore.selectedOrganizationId();
            if (!organizationId) {
                return router.createUrlTree(['/groups', 'settings']);
            }
            const restaurantIds = restaurantsStore.restaurantsOfSelectedOrganization().map((restaurant) => restaurant.id);
            return organizationPermissionsService.canReadUsersOrRoles({ organizationId, restaurantIds })
                ? true
                : router.createUrlTree(['/groups', 'settings']);
        })
    );
};
